import {
  CHECK_PATH,
  FLAME_PATH,
  type Overlay,
  type PhotoEdit,
  type PlacedSticker,
  type Rect,
  type StickerSpec,
  applyColorMatrix,
  coverCrop,
  fitWithin,
  photoFilter,
  stickerColors,
  stickerMetrics,
} from '@goaltree/ui/lib/photo-effects'

export function loadImage(src: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(src)
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image()
    el.onload = () => resolve(el)
    el.onerror = reject
    el.src = url
  }).finally(() => URL.revokeObjectURL(url))
}

function drawOverlays(ctx: CanvasRenderingContext2D, overlays: readonly Overlay[], r: Rect) {
  for (const o of overlays) {
    ctx.save()
    ctx.globalAlpha = o.opacity
    if (o.kind === 'fill') ctx.fillStyle = o.color
    else if (o.kind === 'linear') {
      const g = ctx.createLinearGradient(0, r.y, 0, r.y + r.h)
      g.addColorStop(0, o.from)
      g.addColorStop(1, o.to)
      ctx.fillStyle = g
    } else {
      const cx = r.x + r.w / 2
      const cy = r.y + r.h / 2
      const outer = Math.hypot(r.w, r.h) / 2
      const g = ctx.createRadialGradient(cx, cy, outer * 0.5, cx, cy, outer)
      g.addColorStop(0, 'rgba(0,0,0,0)')
      g.addColorStop(1, o.color)
      ctx.fillStyle = g
    }
    ctx.fillRect(r.x, r.y, r.w, r.h)
    ctx.restore()
  }
}

/**
 * Draws the image filling `r` (which may overflow the canvas, for a cropped
 * preview), then the filter: the colour matrix on the visible pixels and the
 * overlays laid out over the whole image so the preview matches the bake.
 */
export function drawFiltered(
  ctx: CanvasRenderingContext2D,
  img: CanvasImageSource,
  r: Rect,
  filterId: string,
) {
  const f = photoFilter(filterId)
  const { width, height } = ctx.canvas
  ctx.clearRect(0, 0, width, height)
  ctx.drawImage(img, r.x, r.y, r.w, r.h)
  if (f.matrix) {
    const data = ctx.getImageData(0, 0, width, height)
    applyColorMatrix(data.data, f.matrix)
    ctx.putImageData(data, 0, 0)
  }
  drawOverlays(ctx, f.overlays, r)
}

const fontStack = () =>
  (typeof document !== 'undefined' && getComputedStyle(document.body).fontFamily) ||
  'system-ui, sans-serif'

const MAX_CHARS = 28
const clip = (s: string) => (s.length > MAX_CHARS ? `${s.slice(0, MAX_CHARS - 1).trimEnd()}…` : s)

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) {
  const r = Math.min(h, w) / 2
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

function strokeIcon(
  ctx: CanvasRenderingContext2D,
  path: string,
  x: number,
  y: number,
  size: number,
  color: string,
  width = 2.25,
) {
  ctx.save()
  ctx.translate(x, y)
  ctx.scale(size / 24, size / 24)
  ctx.lineWidth = width
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.strokeStyle = color
  ctx.stroke(new Path2D(path))
  ctx.restore()
}

/** Draws one sticker centred in `frame` and returns its box. */
export function drawSticker(
  ctx: CanvasRenderingContext2D,
  spec: StickerSpec,
  placed: PlacedSticker,
  frame: Rect,
): Rect {
  const m = stickerMetrics(Math.min(frame.w, frame.h), placed.size)
  const cx = frame.x + placed.x * frame.w
  const cy = frame.y + placed.y * frame.h
  const c = stickerColors(spec, placed.tone)
  ctx.save()
  ctx.shadowColor = 'rgba(0,0,0,0.22)'
  ctx.shadowBlur = m.font * 0.5
  ctx.shadowOffsetY = m.font * 0.08
  if (spec.kind === 'dot') {
    const d = m.dot
    ctx.beginPath()
    ctx.arc(cx, cy, d / 2, 0, Math.PI * 2)
    ctx.fillStyle = placed.tone === 'dark' ? 'rgba(11,12,11,0.72)' : '#ffffff'
    ctx.fill()
    ctx.shadowColor = 'transparent'
    ctx.beginPath()
    ctx.arc(cx, cy, d * 0.42, 0, Math.PI * 2)
    ctx.fillStyle = spec.color
    ctx.fill()
    if (spec.glyph) {
      ctx.font = `${d * 0.46}px ${fontStack()}`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(spec.glyph, cx, cy + d * 0.03)
    } else strokeIcon(ctx, CHECK_PATH, cx - d * 0.24, cy - d * 0.24, d * 0.48, '#0b0c0b', 3)
    ctx.restore()
    return { x: cx - d / 2, y: cy - d / 2, w: d, h: d }
  }
  ctx.font = `800 ${m.font}px ${fontStack()}`
  const text = clip(spec.text)
  const tw = ctx.measureText(text).width
  const lead =
    spec.kind === 'streak'
      ? m.icon
      : spec.kind === 'habit' && placed.tone === 'dark'
        ? m.font * 0.7
        : 0
  const w = m.padX * 2 + tw + (lead ? lead + m.gap : 0)
  const x = cx - w / 2
  const y = cy - m.height / 2
  roundRect(ctx, x, y, w, m.height)
  ctx.fillStyle = c.bg
  ctx.fill()
  ctx.shadowColor = 'transparent'
  let tx = x + m.padX
  if (spec.kind === 'streak') {
    strokeIcon(ctx, FLAME_PATH, tx, cy - m.icon / 2, m.icon, c.accent)
    tx += m.icon + m.gap
  } else if (lead) {
    ctx.beginPath()
    ctx.arc(tx + lead / 2, cy, lead / 2, 0, Math.PI * 2)
    ctx.fillStyle = c.accent
    ctx.fill()
    tx += lead + m.gap
  }
  ctx.fillStyle = c.fg
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, tx, cy + m.font * 0.04)
  ctx.restore()
  return { x, y, w, h: m.height }
}

export function drawStickers(
  ctx: CanvasRenderingContext2D,
  edit: PhotoEdit,
  specs: readonly StickerSpec[],
  frame: Rect,
) {
  const boxes: { kind: PlacedSticker['kind']; box: Rect }[] = []
  for (const s of edit.stickers) {
    const spec = specs.find((x) => x.kind === s.kind)
    if (spec) boxes.push({ kind: s.kind, box: drawSticker(ctx, spec, s, frame) })
  }
  return boxes
}

/**
 * The edited photo as a JPEG: the whole image at most `max` px on its long
 * side, the filter over all of it and the stickers where they sat in the
 * `aspect` frame the editor showed.
 */
export async function bakePhoto(
  src: Blob,
  edit: PhotoEdit,
  specs: readonly StickerSpec[],
  { max, quality, aspect }: { max: number; quality: number; aspect: number },
): Promise<Blob> {
  const img = await loadImage(src)
  const out = fitWithin(img.naturalWidth, img.naturalHeight, max)
  const canvas = document.createElement('canvas')
  canvas.width = out.w
  canvas.height = out.h
  const ctx = canvas.getContext('2d')!
  drawFiltered(ctx, img, { x: 0, y: 0, w: out.w, h: out.h }, edit.filter)
  drawStickers(ctx, edit, specs, coverCrop(out.w, out.h, aspect))
  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode'))), 'image/jpeg', quality),
  )
}
